// --------------------------------------------------------------
// Created On: 2021-05-14
// Author: Zachary Thomas
//
// Last Modified: 2026-01-05
// Modified By: Hannah Vaughan
//
// Copyright 2024 - 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useState, useEffect, Fragment } from "react";
import { PUBLIC_PAGES, RPM_COMPANY } from "./constants/miscellaneous";
import { Route, Routes, useLocation, useNavigate, Navigate } from "react-router-dom";
import Navbar from "./components/Navbar/Navbar";
import NavigationDrawer from "./components/NavigationDrawer/NavigationDrawer";
import Footer from "./components/Footer/Footer";
import Error404Page from "./pages/Error404Page/Error404Page";
import Error500Page from "./pages/Error500Page/Error500Page";
import LoginPage from "./pages/LoginPage/LoginPage";
import DashboardAdminPage from "./pages/DashboardAdminPage/DashboardAdminPage";
import BomLookUpPage from "./pages/BomLookUpPage/BomLookUpPage";
import ShoppingCartPage from "./pages/ShoppingCartPage/ShoppingCartPage";
import ScanQrCodePage from "./pages/ScanQrCodePage/ScanQrCodePage";
import DashboardGeneralPage from "./pages/DashboardGeneralPage/DashboardGeneralPage";
import SubmitOrderPage from "./pages/SubmitOrderPage/SubmitOrderPage";
import OrderCompletePage from "./pages/OrderCompletePage/OrderCompletePage";
import CompanyDistributorPage from "./pages/CompanyDistributorPage/CompanyDistributorPage";
import ChangeProfilePage from "./pages/ChangeProfilePage/ChangeProfilePage";
import ChangePasswordPage from "./pages/ChangePasswordPage/ChangePasswordPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage/ForgotPasswordPage";
import ReviewInvitePage from "./pages/ReviewInvitePage/ReviewInvitePage";
import CreateAccountPage from "./pages/CreateAccountPage/CreateAccountPage";
import ManageCompaniesPage from "./pages/ManageCompaniesPage/ManageCompaniesPage";
import QuoteListPage from "./pages/QuoteListPage/QuoteListPage";
import PolicyPage from "./pages/PolicyPage/PolicyPage";
import useReduxSelector from "src/hooks/useReduxSelector";
import userHasPermission from "./utilities/userHasPermission";
import {
  CREATE_ROLES_PERMISSION,
  CREATE_USER_GROUPS_PERMISSION,
  DELETE_ROLES_PERMISSION,
  DELETE_USERS_PERMISSION,
  DELETE_USER_GROUPS_PERMISSION,
  INVITE_USERS_PERMISSION,
  UPDATE_ROLES_PERMISSION,
  UPDATE_USERS_PERMISSION,
  UPDATE_USER_GROUPS_PERMISSION,
  CREATE_COMPANY_PERMISSION,
  UPDATE_COMPANY_PERMISSION,
  DELETE_COMPANY_PERMISSION,
  PREPARE_QUOTES_PERMISSION,
} from "./constants/permissions";
import ManageUsersPage from "./pages/ManageUsersPage/ManageUsersPage";
import ManageRolesPage from "./pages/ManageRolesPage/ManageRolesPage";
import CompanyPreferencePage from "./pages/CompanyPreferencePage/CompanyPreferencePage";
import OrderHistoryPage from "./pages/OrderHistoryPage/OrderHistoryPage";
import Toast from "./components/Toast/Toast";
import userHasUserPermissions from "./utilities/userHasUserPermissions";
import userHasRolePermissions from "./utilities/userHasRolePermissions";
import "./App.scss";
import QuickOrderPage from "./pages/QuickOrderPage/QuickOrderPage";

// Handles page routing.
export default function App(): Component {
  const [showNavigationDrawer, setShowNavigationDrawer] = useState<boolean>(false);
  const [redirectUrl, setRedirectUrl] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const currentPage = `/${useLocation().pathname.split("/")[1]}`;
  const idToken = useReduxSelector((state) => state.idToken);
  const currentUser = useReduxSelector((state) => state.currentUser);
  const inCompany = currentUser.companyName !== RPM_COMPANY;
  const navigate = useNavigate();

  // If the user is not logged in, and they are not on a public page (QR pages included), bring them to the login page.
  useEffect(() => {
    if (!PUBLIC_PAGES.includes(currentPage) && !getIsQrSerialNumberLookup(currentPage)) {
      if (idToken === null || idToken === "") {
        if (location.pathname !== "/" && location.pathname !== "/index.html") {
          setRedirectUrl(location.pathname);
        }
        navigate("/login");
      }
    }
  }, []);

  // If a user scans a QR code from a pump, direct them to the ScanQrCodePage component.
  useEffect(() => {
    if (getIsQrSerialNumberLookup(currentPage) && currentPage.length > 2) {
      // We know that the first two characters of the string are always '/q', so we can slice(2) to get serial number.
      const serialNumber = currentPage.slice(2);
      const baseRoute = `/serial/${serialNumber}`;
      navigate(baseRoute, { replace: true });
    }
  }, []);

  // Checks if the current URL is from a QR code scan directing the user to a pump serial number.
  function getIsQrSerialNumberLookup(currentPage: string) {
    // This regular expression considers the following examples valid: "/q1230", "/q48201", "/q3019".
    const serialNumberRegex = /^\/q\d+$/;
    return serialNumberRegex.test(currentPage);
  }

  // Based on the user's permissions and the base route, decide where to redirect when
  // clicking the navbar dropdown without making a selection.
  function permissionBasedNavigate(baseRoute: string): string {
    if (baseRoute === "/manage") {
      if (userHasUserPermissions()) {
        return "/manage/users";
      }

      if (userHasRolePermissions()) {
        return "/manage/roles";
      }
    }

    return baseRoute;
  }

  return (
    <Fragment>
      <Navbar currentPage={currentPage} onOpenNavigationDrawer={() => setShowNavigationDrawer(true)} />

      <NavigationDrawer
        currentPage={currentPage}
        showNavigationDrawer={showNavigationDrawer}
        onCloseNavigationDrawer={() => setShowNavigationDrawer(false)}
        onErrorMessageChange={(errorMessage) => setErrorMessage(errorMessage)}
      />

      {/* Error message toast that bubbles up from navigation drawer errors. */}
      <Toast
        title="Error"
        message={errorMessage}
        show={errorMessage.length > 0}
        onClose={() => setErrorMessage("")}
        type="error"
        data-test="app-error-toast"
      />

      <main>
        <div className="app container-fluid px-0">
          <Routes>
            {inCompany ? (
              <Fragment>
                {/* These are the routes/pages we have access to once we are inside of a company account. */}
                <Route path="/" element={<Navigate to="/dashboard" replace={true} />} />
                <Route path="/dashboard" element={<Navigate to="/dashboard/general" replace={true} />} />
                <Route path="/dashboard/companies" element={<Navigate to="/dashboard/general" replace={true} />} />
                <Route path="/dashboard/general" element={<DashboardGeneralPage />} />
                <Route path="/shopping-cart" element={<ShoppingCartPage />} />
                <Route path="/submit-order" element={<SubmitOrderPage isNewQuote={false} />} />
                <Route path="/quick-order" element={<QuickOrderPage />} />
                <Route path="/order-complete/:orderNumber" element={<OrderCompletePage />} />
                <Route path="/order-history" element={<OrderHistoryPage />} />
                <Route path="/manage-company-distributors" element={<CompanyDistributorPage />} />
                {/* Sales Quotes */}
                {userHasPermission([[PREPARE_QUOTES_PERMISSION]]) && (
                  <Fragment>
                    <Route path="/sales-quotes" element={<QuoteListPage />} />
                    <Route path="/submit-order/quote/:quoteId" element={<SubmitOrderPage isNewQuote={false} />} />
                    <Route path="/submit-order/quote" element={<SubmitOrderPage isNewQuote={true} />} />
                  </Fragment>
                )}
                {/* Management. */}
                <Route path="/manage" element={<Navigate to={permissionBasedNavigate("/manage")} replace={true} />} />
                {userHasPermission([
                  [INVITE_USERS_PERMISSION],
                  [UPDATE_USERS_PERMISSION],
                  [DELETE_USERS_PERMISSION],
                  [CREATE_USER_GROUPS_PERMISSION],
                  [UPDATE_USER_GROUPS_PERMISSION],
                  [DELETE_USER_GROUPS_PERMISSION],
                ]) && <Route path="/manage/users" element={<ManageUsersPage />} />}

                {userHasPermission([
                  [CREATE_ROLES_PERMISSION],
                  [UPDATE_ROLES_PERMISSION],
                  [DELETE_ROLES_PERMISSION],
                ]) && <Route path="/manage/roles" element={<ManageRolesPage />} />}
              </Fragment>
            ) : (
              <Fragment>
                {/* These are the routes/pages that we have access to as a super admin before we select a company account. */}
                <Route path="/" element={<Navigate to="/dashboard" replace={true} />} />
                <Route path="/dashboard" element={<Navigate to="/dashboard/companies" replace={true} />} />
                <Route path="/dashboard/companies" element={<DashboardAdminPage />} />
                <Route path="/shopping-cart" element={<ShoppingCartPage />} />
                <Route path="/submit-order" element={<SubmitOrderPage isNewQuote={false} />} />
                <Route path="/quick-order" element={<QuickOrderPage />} />
                <Route path="/order-complete/:orderNumber" element={<OrderCompletePage />} />
                <Route path="/manage" element={<Navigate to={permissionBasedNavigate("/manage")} replace={true} />} />
                {userHasPermission([[UPDATE_COMPANY_PERMISSION]]) && (
                  <Route path="/manage-company-distributors" element={<CompanyDistributorPage />} />
                )}
                {userHasPermission([
                  [CREATE_COMPANY_PERMISSION],
                  [UPDATE_COMPANY_PERMISSION],
                  [DELETE_COMPANY_PERMISSION],
                ]) && <Route path="/manage/companies" element={<ManageCompaniesPage />} />}
                {userHasUserPermissions() && <Route path="/manage/users" element={<ManageUsersPage />} />}
                {userHasRolePermissions() && <Route path="/manage/roles" element={<ManageRolesPage />} />}
              </Fragment>
            )}

            {/* Account settings. */}
            <Route path="/account" element={<Navigate to="/account/user-info" replace={true} />} />
            <Route path="/account/user-info" element={<ChangeProfilePage />} />
            <Route path="/account/password" element={<ChangePasswordPage />} />

            <Route path={`/company/${currentUser.companyId}/preference`} element={<CompanyPreferencePage />} />

            <Route
              path="/login"
              element={
                <LoginPage
                  redirectUrl={redirectUrl}
                  onRedirect={() => setRedirectUrl("")}
                  isForgotPasswordRedirect={false}
                  authorizationError={false}
                  policyError={false}
                />
              }
            />
            <Route
              path="/login/auth"
              element={
                <LoginPage
                  redirectUrl={redirectUrl}
                  onRedirect={() => setRedirectUrl("")}
                  isForgotPasswordRedirect={false}
                  authorizationError={true}
                  policyError={false}
                />
              }
            />
            <Route
              path="/login/policy-rejected"
              element={
                <LoginPage
                  redirectUrl={redirectUrl}
                  onRedirect={() => setRedirectUrl("")}
                  isForgotPasswordRedirect={false}
                  authorizationError={false}
                  policyError={true}
                />
              }
            />
            <Route
              path="/login/temporary-password"
              element={
                <LoginPage
                  redirectUrl={redirectUrl}
                  onRedirect={() => setRedirectUrl("")}
                  isForgotPasswordRedirect={true}
                  authorizationError={false}
                  policyError={false}
                />
              }
            />

            <Route path="/lookup/:type/:identifier" element={<BomLookUpPage />} />
            <Route path="/serial/:serialNumber" element={<ScanQrCodePage />} />

            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/accept-invite/:emailAddress/:companyCode/:companyName" element={<ReviewInvitePage />} />
            <Route path="/register" element={<CreateAccountPage />} />
            <Route path="/privacy-policy-text" element={<PolicyPage policyType="Privacy Policy" />} />
            <Route
              path="/terms-and-conditions-of-sale-text"
              element={<PolicyPage policyType="Terms and Conditions of Sale" />}
            />

            <Route path="/error-500" element={<Error500Page />} />
            <Route path="*" element={<Error404Page />} />
          </Routes>
        </div>
      </main>

      <Footer />
    </Fragment>
  );
}
